import React from 'react';
import { Helmet } from 'react-helmet';
import { Clock, User, Calendar, Tag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogLayout = ({
  title,
  metaDescription,
  author = "AddictionRehabCall Team",
  publishedDate,
  updatedDate,
  readTime,
  category,
  tags = [],
  featuredImage,
  featuredImageAlt,
  children,
  relatedPosts = []
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": metaDescription,
    "author": {
      "@type": "Organization",
      "name": "AddictionRehabCall"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AddictionRehabCall",
      "logo": {
        "@type": "ImageObject",
        "url": "https://addictionrehabcall.com/logo.png"
      }
    },
    "datePublished": publishedDate,
    "dateModified": updatedDate || publishedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'treatment-guides': 'bg-blue-100 text-blue-800',
      'recovery-resources': 'bg-green-100 text-green-800',
      'addiction-types': 'bg-orange-100 text-orange-800',
      'family-support': 'bg-red-100 text-red-800',
      'prevention': 'bg-purple-100 text-purple-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryName = (category) => {
    const names = {
      'treatment-guides': 'Treatment Guides',
      'recovery-resources': 'Recovery Resources',
      'addiction-types': 'Addiction Types',
      'family-support': 'Family Support',
      'prevention': 'Prevention'
    };
    return names[category] || category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {featuredImage && <meta property="og:image" content={featuredImage} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {featuredImage && <meta name="twitter:image" content={featuredImage} />}
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/blog" className="text-gray-500 hover:text-gray-700">Blog</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{getCategoryName(category)}</span>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Article Header */}
        <header className="mb-8">
          <div className="mb-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(category)}`}>
              {getCategoryName(category)}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-6">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              {author}
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              {formatDate(publishedDate)}
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              {readTime} min read
            </div>
          </div>

          {featuredImage && (
            <div className="mb-8">
              <img
                src={featuredImage}
                alt={featuredImageAlt || title}
                className="w-full h-64 md:h-80 object-cover rounded-lg shadow-lg"
                loading="eager"
              />
            </div>
          )}
        </header>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <div className="blog-content">
            {children}
          </div>
        </div>

        {/* Article Footer */}
        <footer className="mt-12 pt-8 border-t">
          {tags.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-3">
                <Tag className="h-4 w-4 mr-2" />
                <span className="font-medium text-gray-900">Tags:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag.toLowerCase().replace(' ', '-'))}`}
                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1 rounded-full text-sm transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 mb-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Need Help Finding Treatment?
              </h3>
              <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                Our directory includes 300+ verified treatment centers across all 50 states. 
                Get personalized recommendations and immediate support from our team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/search"
                  className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Find Treatment Centers
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <a
                  href="tel:1-888-968-1014"
                  className="inline-flex items-center justify-center bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                >
                  Call 1-888-968-1014
                </a>
              </div>
              
              {/* Trust Indicators */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                <div className="flex items-center justify-center">
                  <span className="text-green-600 mr-1">✓</span>
                  24/7 Support
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-green-600 mr-1">✓</span>
                  Insurance Verification
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-green-600 mr-1">✓</span>
                  Same-Day Placement
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-green-600 mr-1">✓</span>
                  Confidential Support
                </div>
              </div>
            </div>
          </div>

          {/* Author Bio */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{author}</h4>
                <p className="text-gray-600">
                  Our team of addiction treatment specialists and recovery advocates is dedicated to 
                  helping people find the right treatment options for lasting recovery. We provide 
                  evidence-based information and compassionate support to individuals and families 
                  affected by addiction.
                </p>
              </div>
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="border-t pt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map(post => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group block bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {post.featured_image && (
                      <div className="aspect-video bg-gray-200 overflow-hidden">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {post.read_time} min read
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <Link
                  to="/blog"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Articles
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </footer>
      </article>
    </>
  );
};

export default BlogLayout;